import {
  Autocomplete,
  Box,
  Switch,
  TextField,
  FormHelperText,
  FormGroup,
  FormControlLabel,
  Divider,
  Checkbox,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Stack,
} from '@mui/material'
import {
  IntegrationConfig,
  IntegrationConfigSettingsJazzHRSyncApplicants,
  OperatorEnum,
  Field1Enum,
  IntegrationConfigSettingsJazzHRSyncApplicantsConditionGroup,
  ConditionOperator,
} from '@sendhub/integrations/api-client-typescript-axios'
import { getEnumValues } from '../../../../utils/get-enum-values'
import * as yup from 'yup'

import { useEffect, useState } from 'react'
import { updateIntegrationConfig } from '../../../store/integration-config/integration-configs.slice'
import { useAppDispatch } from '../../../store/root'
import { FieldArray, FieldArrayRenderProps, FormikProvider, useFormik, useFormikContext } from 'formik'
import SelectAccount from '../../select-account'
import { FormSaveControls } from '../../save-status-indicator'
import { applyConfigUpdateError } from '../apply-config-update-error'
import { IntegrationsEnabledSwitch } from '../../integrations-enabled-switch'
import { useIntegrationsAccess } from '../../../store/integrations-access'
import { AddCircleOutlineOutlined, DeleteOutlined, QueueOutlined } from '@mui/icons-material'
import lodash from 'lodash'
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useSelector } from 'react-redux'
import { selectAllUsers, selectUsersIds, selectUsersLoadingStatus } from '../../../store/sendhub-users/sendhub-users.slice'

const shakeCss = css`
  @keyframes shakeKf {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0);
    }

    20%,
    80% {
      transform: translate3d(2px, 0, 0);
    }

    30%,
    50%,
    70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%,
    60% {
      transform: translate3d(4px, 0, 0);
    }
  }

  animation: shakeKf 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  backface-visibility: hidden;
  perspective: 1000px;
`
const noShakeCss = css`
  transform: translate3d(0, 0, 0);
`

export function isJazzhrSyncApplicantsSettings(obj: unknown): obj is IntegrationConfigSettingsJazzHRSyncApplicants {
  return typeof obj === 'object' && obj !== null && 'conditionGroups' in obj && 'lastNameFirst' in obj
}
const validationSchema = yup.object({
  enabled: yup.boolean().default(false),
  integrationId: yup.string().required(),
  settings: yup.object({
    lastNameFirst: yup.boolean().required().default(false),
    conditionGroups: yup.array().of(
      yup.object({
        conditions: yup.array().of(
          yup.object({
            field1: yup.string().oneOf(getEnumValues(Field1Enum)).required('Required').label('Applicant Field'),
            operator: yup.string().oneOf(getEnumValues(OperatorEnum)).required('Required').label('operator'),
            field2: yup.string().label('Compared To'), // Can be blank
          })
        ),
        lines: yup.array().of(yup.string()).min(1, 'At least 1 SendHub line is required'),
        conditionOperator: yup.string().oneOf(['AND', 'OR']).default('AND').required(),
      })
    ),
  }),
  serviceAccountId: yup.string().required('Follow Up Boss Account Required').uuid(),
})

const emptyCondition = {
  field1: '',
  operator: '',
  field2: '',
}
const emptyConditionGroup = {
  conditions: [emptyCondition],
  lines: [],
  conditionOperator: 'AND',
}

const jazzhrFieldsMap: Record<string, string> = {
  'applicant.first_name': 'Applicant First Name',
  'applicant.last_name': 'Applicant Last Name',
  'applicant.prospect_phone': 'Applicant Phone Number',
  'applicant.apply_date': 'Applicant Apply Date (YYYY-MM-DD)',
  'applicant.job_title': 'Job Title',
  'job.id': 'Job ID',
  'job.teamId': 'Job Team ID',
  'job.countryId': 'Job Country',
  'job.city': 'Job City',
  'job.state': 'Job State',
  'job.zip': 'Job Zip Code',
  'job.department': 'Job Department',
  'job.description': 'Job Description',
  'job.minimum_salary': 'Job Minimum Salary',
  'job.maximum_salary': 'Job  Maximum Salary',
  'job.notes': 'Job Notes',
  'job.original_open_date': 'Job Original Open Date (YYYY-MM-DD)',
  'job.type': 'Job Type',
  'job.status': 'Job Status',
  'job.send_to_job_boards': 'Job Send To Job Boards',
  'job.hiring_lead': 'Job Hiring Lead',
  'job.board_code': 'Job Board Code',
  'job.internal_code': 'Job Internal Code',
  'job.questionnaire': 'Job Questionnaire ID',
}

export interface ConfigureJazzhrSyncApplicantsProps {
  integrationConfig: IntegrationConfig
}

export function ConfigureJazzhrSyncApplicants({ integrationConfig }: ConfigureJazzhrSyncApplicantsProps) {
  const dispatch = useAppDispatch()
  const { canEdit } = useIntegrationsAccess()

  const startingSettings = (integrationConfig: IntegrationConfig): IntegrationConfigSettingsJazzHRSyncApplicants => {
    const existingSettings = isJazzhrSyncApplicantsSettings(integrationConfig.settings) ? integrationConfig.settings : {}
    const defaultConditionGroups = [
      {
        conditionOperator: ConditionOperator.AND,
        conditions: [
          {
            field1: '' as Field1Enum,
            operator: OperatorEnum.Equals,
            field2: '' as OperatorEnum,
          },
        ],
        lines: [],
      },
    ]
    const newSettings: IntegrationConfigSettingsJazzHRSyncApplicants = {
      lastNameFirst: false,
      conditionGroups: defaultConditionGroups,
      ...existingSettings,
    }
    if (!newSettings.conditionGroups.length) newSettings.conditionGroups = defaultConditionGroups

    return newSettings
  }
  const [initialValues, setInitialValues] = useState({ ...integrationConfig, settings: startingSettings(integrationConfig) })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, setFieldError, setFieldTouched, resetForm }) => {
      if (values === undefined) return
      setStatus(null)
      try {
        await dispatch(updateIntegrationConfig({ id: values.id, integrationConfig: values })).unwrap()
        resetForm({ values, status: { type: 'success' } })
      } catch (error) {
        applyConfigUpdateError(error, { setStatus, setFieldError, setFieldTouched })
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({ values: { ...integrationConfig, settings: startingSettings(integrationConfig) } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationConfig])

  // Add a condition group if all are deleted
  useEffect(() => {
    if (formik.values.settings.conditionGroups.length < 1) {
      formik.setFieldValue('settings.conditionGroups', [emptyConditionGroup])
    }
  }, [formik.values.settings.conditionGroups.length])

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <SelectAccount integrationConfig={integrationConfig} />
        <Divider sx={{ marginY: 2 }} />

        <FormControlLabel
          disabled={!canEdit}
          control={
            <Checkbox
              color="primary"
              checked={formik.values.settings.lastNameFirst}
              onChange={(e) => {
                formik.handleChange(e)
                formik.setFieldValue('lastNameFirst', !formik.values.settings.lastNameFirst)
              }}
              id="settings.lastNameFirst"
              name="settings.lastNameFirst"
            />
          }
          label="Save contact names as Last, First"
          labelPlacement="end"
        />

        <TableContainer component={Paper}>
          <FieldArray name="settings.conditionGroups">
            {(fieldArrayRenderProps) => (
              <>
                <Table aria-label="Condition Groups table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Condition(s)</TableCell>
                      <TableCell>SendHub Line(s)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formik.values?.settings?.conditionGroups?.map &&
                      formik.values.settings.conditionGroups.map((conditionGroup, cgIndex) => (
                        <ConditionGroup
                          conditionGroup={conditionGroup}
                          key={cgIndex}
                          cgIndex={cgIndex}
                          fieldArrayRenderProps={fieldArrayRenderProps}
                        />
                      ))}
                  </TableBody>
                </Table>
                <Box sx={{ textAlign: 'center' }}>
                  {canEdit && (
                  <Tooltip title="Add Condition Group" placement="top">
                    <Button
                      aria-label={'Add Condition Group'}
                      onClick={() => {
                        fieldArrayRenderProps.push(emptyConditionGroup)
                      }}
                      style={{
                        padding: '0px',
                      }}
                      size={'small'}
                    >
                      <AddCircleOutlineOutlined />
                    </Button>
                  </Tooltip>
                  )}
                </Box>
              </>
            )}
          </FieldArray>
        </TableContainer>

        {/* Enable switch */}
        <FormGroup>
          <IntegrationsEnabledSwitch />
        </FormGroup>
        <FormSaveControls />
      </form>
    </FormikProvider>
  )
}

function ConditionGroup({
  cgIndex,
  conditionGroup,
  fieldArrayRenderProps,
}: {
  conditionGroup: IntegrationConfigSettingsJazzHRSyncApplicantsConditionGroup
  cgIndex: number
  fieldArrayRenderProps: FieldArrayRenderProps
}) {
  const formik = useFormikContext<
    IntegrationConfig & {
      settings: IntegrationConfigSettingsJazzHRSyncApplicants
    }
  >()

  const linesName = `settings.conditionGroups.${cgIndex}.lines`
  const linesMeta = formik.getFieldMeta(linesName)

  const sendhubUserIds = useSelector(selectUsersIds)
  const sendhubUsers = useSelector(selectAllUsers)
  const sendhubUsersLoadingStatus = useSelector(selectUsersLoadingStatus)
  const { canEdit } = useIntegrationsAccess()

  const [shouldShake, setShouldShake] = useState(false)

  // Set shouldShake to true when form isn't valid
  useEffect((): (() => void) | void => {
    if (!formik.isValid) {
      setShouldShake(true)
      const timeout = setTimeout(() => setShouldShake(false), 820)
      return () => clearTimeout(timeout)
    }
  }, [formik.submitCount])

  // If conditionGroup.conditions is empty, add a blank condition
  useEffect(() => {
    if (conditionGroup.conditions.length == 0) {
      formik.setFieldValue(`settings.conditionGroups[${cgIndex}].conditions`, [emptyCondition])
    }
  }, [conditionGroup.conditions.length, formik.setFieldValue])
  return (
    <TableRow key={cgIndex}>
      {/* This cell contains a nested table with rows of conditions */}
      <TableCell>
        {/* Condition Group title and buttons */}
        <Stack direction="row">
          <Typography sx={{ marginRight: 1 }}>Condition Group {cgIndex + 1}</Typography>
          {canEdit && (
          <Tooltip title="Duplicate Condition Group" placement="top">
            <IconButton
              aria-label={'Duplicate Condition Group'}
              onClick={() => {
                const updatedValues = lodash.cloneDeep(formik.values)
                updatedValues.settings.conditionGroups.splice(cgIndex + 1, 0, conditionGroup)
                formik.setValues(updatedValues)
              }}
              sx={{ paddingY: 0 }}
              size={'small'}
            >
              <QueueOutlined />
            </IconButton>
          </Tooltip>
          )}
          {canEdit && (
          <Tooltip title="Delete Condition Group" placement="top">
            <IconButton
              aria-label={'Delete Condition Group'}
              onClick={() => fieldArrayRenderProps.remove(cgIndex)}
              sx={{ paddingY: 0 }}
              size={'small'}
            >
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
          )}
        </Stack>

        {/* Table of individual conditions */}
        <TableContainer component={Paper}>
          <FieldArray name={`settings.conditionGroups.${cgIndex}.conditions`}>
            {(fieldArrayRenderProps) => (
              <>
                <Table size={'small'}>
                  <colgroup>
                    <col span={1} style={{ width: '30%' }} />
                    <col span={1} style={{ width: '30%' }} />
                    <col span={1} style={{ width: '30%' }} />
                    <col span={1} style={{ width: '9%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell>Applicant Field</TableCell>
                      <TableCell>Operator</TableCell>
                      <TableCell>Compared To</TableCell>
                      <TableCell style={{ padding: '0px' }}>
                        <ToggleButtonGroup
                          size="small"
                          id={`settings.conditionGroups.${cgIndex}.conditionOperator`}
                          value={formik.values.settings.conditionGroups[cgIndex].conditionOperator}
                          exclusive
                          onChange={(e, value) => {
                            value && formik.setFieldValue(`settings.conditionGroups.${cgIndex}.conditionOperator`, value)
                          }}
                        >
                          <Tooltip title="All of these conditions must be true" placement="top">
                            <ToggleButton value="AND" selected={formik.values.settings.conditionGroups[cgIndex].conditionOperator == 'AND'}>
                              AND
                            </ToggleButton>
                          </Tooltip>
                          <Tooltip title="At least one of these conditions must be true" placement="top">
                            <ToggleButton value="OR" selected={formik.values.settings.conditionGroups[cgIndex].conditionOperator == 'OR'}>
                              OR
                            </ToggleButton>
                          </Tooltip>
                        </ToggleButtonGroup>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conditionGroup.conditions.map((condition, cIndex) => {
                      return <ConditionRow cgIndex={cgIndex} cIndex={cIndex} key={cIndex} fieldArrayRenderProps={fieldArrayRenderProps} />
                    })}
                  </TableBody>
                </Table>

                {/* Add Condition button */}
                <Box sx={{ textAlign: 'center' }}>
                  {canEdit && (
                  <Tooltip title="Add Condition" placement="top">
                    <Button
                      aria-label={'Add Condition'}
                      onClick={() => {
                        formik.setFieldValue(`settings.conditionGroups.${cgIndex}.conditions`, [
                          ...formik.values.settings.conditionGroups[cgIndex].conditions,
                          emptyCondition,
                        ])
                      }}
                      size={'small'}
                    >
                      <AddCircleOutlineOutlined />
                    </Button>
                  </Tooltip>
                  )}
                </Box>
              </>
            )}
          </FieldArray>
        </TableContainer>
      </TableCell>

      {/* This cell contains a drop-down with a list of SendHub lines to apply the conditions to */}
      <TableCell>
        <Autocomplete
          id="lines"
          multiple
          disableClearable
          blurOnSelect
          openOnFocus
          disabled={!canEdit}
          fullWidth
          sx={{ maxWidth: 200 }}
          value={formik.values.settings.conditionGroups[cgIndex].lines}
          loading={sendhubUsersLoadingStatus === 'loading'}
          onChange={(event, value) => {
            formik.setFieldValue(linesName, value)
          }}
          options={sendhubUserIds}
          getOptionLabel={(selectedUserId) =>
            sendhubUsers.find((user) => user.user_id === selectedUserId)?.name || `Line: ${selectedUserId}`
          }
          renderInput={(params) => (
            <TextField
              {...params}
              id={linesName}
              name={linesName}
              label={'SendHub Line(s) to sync to'}
              variant="outlined"
              css={linesMeta.touched && Boolean(linesMeta.error) && shouldShake ? shakeCss : noShakeCss}
              error={linesMeta.touched && Boolean(linesMeta.error)}
              helperText={linesMeta.error}
            />
          )}
        />
      </TableCell>
    </TableRow>
  )
}

function ConditionRow({
  cgIndex,
  cIndex,
  fieldArrayRenderProps,
}: {
  cgIndex: number
  cIndex: number
  fieldArrayRenderProps: FieldArrayRenderProps
}) {
  const { canEdit } = useIntegrationsAccess()
  const formik = useFormikContext<
    IntegrationConfig & {
      settings: IntegrationConfigSettingsJazzHRSyncApplicants
    }
  >()
  const field1Name = `settings.conditionGroups[${cgIndex}].conditions[${cIndex}].field1`
  const field2Name = `settings.conditionGroups[${cgIndex}].conditions[${cIndex}].field2`
  const operatorName = `settings.conditionGroups[${cgIndex}].conditions[${cIndex}].operator`

  const field1Meta = formik.getFieldMeta(field1Name)
  const field2Meta = formik.getFieldMeta(field2Name)
  const operatorMeta = formik.getFieldMeta(operatorName)

  const [shouldShake, setShouldShake] = useState(false)

  // Set shouldShake to true when form isn't valid
  useEffect((): (() => void) | void => {
    if (!formik.isValid) {
      setShouldShake(true)
      const timeout = setTimeout(() => setShouldShake(false), 820)
      return () => clearTimeout(timeout)
    }
  }, [formik.submitCount])
  return (
    <TableRow>
      {/* field1 */}
      <TableCell>
        <Autocomplete
          id={field1Name}
          disableClearable
          fullWidth
          disabled={!canEdit}
          value={formik.values.settings.conditionGroups[cgIndex].conditions[cIndex].field1}
          onChange={(event, value) => formik.setFieldValue(field1Name, value)}
          onBlur={formik.handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              id={field1Name}
              key={field1Name}
              name={field1Name}
              label={'Applicant Field'}
              variant="standard"
              css={field1Meta.touched && Boolean(field1Meta.error) && shouldShake ? shakeCss : noShakeCss}
              error={field1Meta.touched && Boolean(field1Meta.error)}
              helperText={field1Meta.error}
            />
          )}
          getOptionLabel={(option) => jazzhrFieldsMap[option] || ''}
          options={Object.keys(jazzhrFieldsMap)}
        />
      </TableCell>
      {/* operator */}
      <TableCell>
        <Autocomplete
          id={operatorName}
          disableClearable
          fullWidth
          disabled={!canEdit}
          value={formik.values.settings.conditionGroups[cgIndex].conditions[cIndex].operator}
          onChange={(event, value) => formik.setFieldValue(operatorName, value)}
          onBlur={formik.handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              id={operatorName}
              key={operatorName}
              name={operatorName}
              label={'Operator'}
              variant="standard"
              css={operatorMeta.touched && Boolean(operatorMeta.error) && shouldShake ? shakeCss : noShakeCss}
              error={operatorMeta.touched && Boolean(operatorMeta.error)}
              helperText={operatorMeta.error}
            />
          )}
          options={getEnumValues(OperatorEnum)}
        />
      </TableCell>
      {/* field2 */}
      <TableCell>
        <TextField
          id={field2Name}
          key={field2Name}
          name={field2Name}
          label={'Operator'}
          variant="standard"
          disabled={!canEdit}
          value={formik.values.settings.conditionGroups[cgIndex].conditions[cIndex].field2}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          css={field2Meta.touched && Boolean(field2Meta.error) && shouldShake ? shakeCss : noShakeCss}
          error={field2Meta.touched && Boolean(field2Meta.error)}
          helperText={field2Meta.error}
        />
      </TableCell>
      {/* Duplicate and delete buttons */}
      <TableCell sx={{ p: 1 }}>
        {canEdit && (
        <Tooltip title="Duplicate Condition" placement="top">
          <IconButton
            aria-label={'Duplicate Condition'}
            onClick={() => {
              fieldArrayRenderProps.insert(cIndex + 1, formik.values.settings.conditionGroups[cgIndex].conditions[cIndex])
            }}
            size={'small'}
          >
            <QueueOutlined />
          </IconButton>
        </Tooltip>
        )}
        {canEdit && (
        <Tooltip title="Delete Condition" placement="top">
          <IconButton
            aria-label={'Delete Condition'}
            onClick={() => {
              fieldArrayRenderProps.remove(cIndex)
            }}
            size={'small'}
          >
            <DeleteOutlined />
          </IconButton>
        </Tooltip>
        )}
      </TableCell>
    </TableRow>
  )
}
